import React from 'react';
import styled from 'styled-components';
import { useFormikContext } from 'formik';

import { StudentOnlineApplicationBlock } from '@modules/students/moleculas';
import { StudentOnlineApplicationStatus } from '@modules/types/graphql';

import type { UpdateStudentOnlineApplicationFormValues } from '@modules/students/requests';

const StudentOnlineApplicationResult = () => {
    const { values } = useFormikContext<UpdateStudentOnlineApplicationFormValues>();

    const { title, description } = React.useMemo(() => {
        const result = {
            title: '',
            description: '' as React.ReactNode,
        };

        if (values.currentStatus === StudentOnlineApplicationStatus.schoolApproved) {
            result.title = 'Допущен со стороны Образовательной организации';
            result.description = (
                <Description>
                    <p>До скорой встречи!</p>
                    <p>
                        <b>
                            Пожалуйста, не забудьте взять с собой на сборы все необходимые
                            документы:
                        </b>
                    </p>
                    <p>
                        <b>Скан-копии:</b>
                    </p>
                    <ul>
                        <li>
                            – Документ, удостоверяющий личность <br />
                            (2–3 страницы, включая страницу с пропиской)
                        </li>
                        <li>
                            – Медицинский страховой полис <br />
                            (главная и оборотная страницы)
                        </li>
                    </ul>

                    <p>
                        <b>Оригиналы с подписями и печатями (медицинские документы):</b>
                    </p>
                    <ul>
                        <li>– Справка 079/у или 086/у</li>
                        <li>
                            – Справка об отсутствии контактов с инфекционными больными (если
                            требуется)
                        </li>
                        <li>– Справка на яйца глистов (если требуется)</li>
                    </ul>

                    <p>
                        <b>Оригиналы с подписями:</b>
                    </p>
                    <ul>
                        <li> – Расписка о возможности прохождения учебных сборов</li>
                        <li> – Согласие на обработку персональных данных</li>
                        <li> – Согласие на медицинское вмешательство</li>
                        <li> – Осмотр личных вещей</li>
                        <li> – Психологическое сопровождение</li>
                        <li> – Анкета обучающегося</li>
                        <li> – Правила внутреннего распорядка</li>
                    </ul>

                    <p>
                        <b>Рекомендуем заранее ознакомиться с важной информацией:</b>
                    </p>
                    <p>
                        Памятка —
                        <a
                            href='https://avangard.center/pamyatka-dlya-roditeley'
                            target='_blank'
                            rel='noreferrer noopener'
                        >
                            https://avangard.center/pamyatka-dlya-roditeley
                        </a>
                    </p>
                </Description>
            );
        }

        if (values.currentStatus === StudentOnlineApplicationStatus.approved) {
            result.title = 'Допущен';
            result.description = (
                <Description>
                    <p>До скорой встречи!</p>
                    <p>
                        <b>
                            Пожалуйста, не забудьте взять с собой на сборы все необходимые
                            документы:
                        </b>
                    </p>
                    <p>
                        <b>Скан-копии:</b>
                    </p>
                    <ul>
                        <li>
                            – Документ, удостоверяющий личность <br />
                            (2–3 страницы, включая страницу с пропиской)
                        </li>
                        <li>
                            – Медицинский страховой полис <br />
                            (главная и оборотная страницы)
                        </li>
                    </ul>

                    <p>
                        <b>Оригиналы с подписями и печатями (медицинские документы):</b>
                    </p>
                    <ul>
                        <li>– Справка 079/у или 086/у</li>
                        <li>
                            – Справка об отсутствии контактов с инфекционными больными (если
                            требуется)
                        </li>
                        <li>– Справка на яйца глистов (если требуется)</li>
                    </ul>

                    <p>
                        <b>Оригиналы с подписями:</b>
                    </p>
                    <ul>
                        <li> – Расписка о возможности прохождения учебных сборов</li>
                        <li> – Согласие на обработку персональных данных</li>
                        <li> – Согласие на медицинское вмешательство</li>
                        <li> – Осмотр личных вещей</li>
                        <li> – Психологическое сопровождение</li>
                        <li> – Анкета обучающегося</li>
                        <li> – Правила внутреннего распорядка</li>
                    </ul>

                    <p>
                        <b>Рекомендуем заранее ознакомиться с важной информацией:</b>
                    </p>
                    <p>
                        Памятка —
                        <a
                            href='https://avangard.center/pamyatka-dlya-roditeley'
                            target='_blank'
                            rel='noreferrer noopener'
                        >
                            https://avangard.center/pamyatka-dlya-roditeley
                        </a>
                    </p>
                </Description>
            );
        }

        if (values.currentStatus === StudentOnlineApplicationStatus.rejected) {
            result.title = 'Отказано';
            result.description = (
                <>
                    <p>Ваша документация рассмотрена, но, к сожалению, вам было отказано.</p>
                    <p>Причину отказа вы можете узнать в чате по анкете.</p>
                </>
            );
        }

        return result;
    }, [values.currentStatus]);

    return <StudentOnlineApplicationBlock title={title} description={description} />;
};

const Description = styled.div`
    margin-top: 24px;
    font-size: 1.4rem;
    line-height: 25px;

    ul {
        margin-bottom: 8px;

        li {
            padding-left: 16px;
        }
    }
`;

export { StudentOnlineApplicationResult };
